/**
 * AddressForm - T072
 *
 * Form for creating/editing addresses
 */
import { useState, type FormEvent } from 'react';
import { Input, Select, Button } from '../ui';
import type { Address } from '../../hooks/useCustomers';
import type { CreateAddressInput } from '@armard/shared';

interface AddressFormProps {
  address?: Address;
  onSubmit: (data: CreateAddressInput) => void;
  isLoading?: boolean;
  onCancel?: () => void;
}

const addressTypeOptions = [
  { value: 'billing', label: 'Rechnungsadresse' },
  { value: 'shipping', label: 'Lieferadresse' },
];

const countryOptions = [
  { value: 'DE', label: 'Deutschland' },
  { value: 'AT', label: 'Österreich' },
  { value: 'CH', label: 'Schweiz' },
];

export function AddressForm({
  address,
  onSubmit,
  isLoading,
  onCancel,
}: AddressFormProps) {
  const [formData, setFormData] = useState({
    addressType: address?.addressType ?? 'billing',
    isDefault: address?.isDefault ?? false,
    street: address?.street ?? '',
    street2: address?.street2 ?? '',
    postalCode: address?.postalCode ?? '',
    city: address?.city ?? '',
    country: address?.country ?? 'DE',
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({
      addressType: formData.addressType as 'billing' | 'shipping',
      isDefault: formData.isDefault,
      street: formData.street,
      street2: formData.street2 || undefined,
      postalCode: formData.postalCode,
      city: formData.city,
      country: formData.country,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Adresstyp"
            name="addressType"
            value={formData.addressType}
            onChange={(e) =>
              setFormData({ ...formData, addressType: e.target.value as 'billing' | 'shipping' })
            }
            options={addressTypeOptions}
          />

          <div className="flex items-center pt-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="w-4 h-4 rounded bg-white/[0.03] border-white/20 text-white/80 focus:ring-white/20 focus:ring-offset-0"
              />
              <span className="text-sm text-white/70">Standard</span>
            </label>
          </div>
        </div>

        <Input
          label="Straße *"
          name="street"
          value={formData.street}
          onChange={(e) => setFormData({ ...formData, street: e.target.value })}
          required
        />

        <Input
          label="Adresszusatz"
          name="street2"
          value={formData.street2}
          onChange={(e) => setFormData({ ...formData, street2: e.target.value })}
        />

        <div className="grid grid-cols-3 gap-4">
          <Input
            label="PLZ *"
            name="postalCode"
            value={formData.postalCode}
            onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
            required
          />
          <div className="col-span-2">
            <Input
              label="Stadt *"
              name="city"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              required
            />
          </div>
        </div>

        <Select
          label="Land"
          name="country"
          value={formData.country}
          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
          options={countryOptions}
        />
      </div>

      <div className="flex justify-end gap-3 mt-4">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Abbrechen
          </Button>
        )}
        <Button type="submit" loading={isLoading}>
          {address ? 'Speichern' : 'Hinzufügen'}
        </Button>
      </div>
    </form>
  );
}

export default AddressForm;
